import './style.css'
import gsap from 'gsap'
import Intro from './Intro.js'
import { captureRefFromURL, track } from './recommendations.js'

// Capture ?ref=<token> from invite links into localStorage on page load.
// Stays there until the visitor finishes their own quiz, at which point
// Report.show() calls consumePendingRef to credit the inviter.
const _capturedRef = captureRefFromURL()
if (_capturedRef) track('invite_link_visit', { ref: _capturedRef })
track('page_load', { path: location.pathname })

// Experience / Keyboard / Quiz / Report are code-split — they stay out of the
// initial JS bundle so the intro paints instantly. The heavy 3D + asset
// fetches only start once the user commits to entering.
const loadExperience = () => import(/* webpackChunkName: "experience" */ './Experience/Experience.js').then(m => m.default)
// Keyboard is the pure-DOM answer panel (only depends on gsap, already in the
// main bundle). Keep it on its OWN chunk — never bundled with the heavy three.js
// "experience" chunk — so the quiz UI still loads if that 3D bundle is blocked.
const loadKeyboard   = () => import(/* webpackChunkName: "keyboard" */ './Experience/Keyboard.js').then(m => m.default)
const loadQuiz       = () => import(/* webpackChunkName: "quiz"    */ './Quiz.js').then(m => m.default)
const loadReport     = () => import(/* webpackChunkName: "report"  */ './Report.js').then(m => m.default)
const loadReportState = () => import(/* webpackChunkName: "report" */ './recommendations.js')

const intro        = document.querySelector('.intro-overlay')
const startBtn     = intro.querySelector('.intro-start')
const startLabel   = startBtn.querySelector('.intro-start__label')
const startArrow   = startBtn.querySelector('.intro-start__arrow')
const resumeToggle = intro.querySelector('.intro-resume-toggle')
const resumeWrap   = intro.querySelector('.intro-resume-wrap')
const resumeForm   = intro.querySelector('.intro-resume')
const resumeInput  = intro.querySelector('.intro-resume-input')
const resumeHint   = intro.querySelector('.intro-resume-hint')

resumeToggle.addEventListener('click', () => {
    const open = !resumeWrap.hidden
    resumeWrap.hidden = open
    resumeToggle.setAttribute('aria-expanded', String(!open))
    if (!open) setTimeout(() => resumeInput.focus(), 30)
})

const introAnim = new Intro()

let started = false
let experience = null
let keyboard   = null
let bootPromise = null

// Boot the pure-DOM answer panel. Loads on its own lightweight chunk so the
// quiz can run even when the heavy 3D bundle / assets are unreachable.
let keyboardPromise = null
function bootKeyboard()
{
    if (keyboardPromise) return keyboardPromise
    keyboardPromise = (async () => {
        const Keyboard = await loadKeyboard()
        keyboard = new Keyboard({ root: document.querySelector('.answer-grid') })
        window.keyboard = keyboard
        return keyboard
    })().catch((err) => {
        keyboardPromise = null   // allow a retry on the next click
        throw err
    })
    return keyboardPromise
}

// Boot the 3D experience. Resolves once world is ready so the caller can safely
// call into experience.world / experience.navigation. Cached — kicked off at
// page load and re-awaited on click, so the user's reading time on the intro
// overlaps with asset loading. A FAILED boot is never cached (so it can't
// permanently block the quiz) and never throws into the page unhandled.
function bootExperience()
{
    if (bootPromise) return bootPromise
    bootPromise = (async () => {
        const Experience = await loadExperience()

        experience = new Experience({ targetElement: document.querySelector('.experience') })
        window.experience = experience

        if (!experience.world?.ready) {
            await new Promise((resolve) => {
                const done = () => { window.removeEventListener('world:ready', done); resolve() }
                window.addEventListener('world:ready', done)
                // 20s safety — fall through even if a resource hangs so the UI
                // doesn't trap the user on the intro screen forever.
                setTimeout(done, 20000)
            })
        }
        return experience
    })().catch((err) => {
        // Reset so a transient failure (e.g. the 3D chunk/assets blocked by a
        // throttling network) can be retried, and never leaves a rejected
        // promise cached to poison every later attempt. This catch is the ONLY
        // place that concludes 3D has truly failed — so it's where we flip to
        // lite mode (a 12s *timeout* elsewhere does not, since boot may still
        // complete and we want the finale to use it once it does).
        bootPromise = null
        experience = null
        window.experience = null
        bootFailedOnce = true
        enterLiteMode('boot', err)
        throw err
    })
    return bootPromise
}

// Set after any failed 3D boot attempt. A user who already sat through one
// failure shouldn't burn another 12s timeout on click — we go lite immediately.
let bootFailedOnce = false

// Resolve to the ready World once it's available, or null if 3D has failed or
// doesn't arrive within `ms`. Lets the finale use a world that finished its
// (slow) boot during the quiz rather than downgrading prematurely.
function waitForWorld(ms = 8000)
{
    if (experience?.world?.ready) return Promise.resolve(experience.world)
    if (bootFailedOnce) return Promise.resolve(null)
    return new Promise((resolve) => {
        let done = false
        const settle = () => {
            if (done) return
            done = true
            window.removeEventListener('world:ready', settle)
            resolve(experience?.world?.ready ? experience.world : null)
        }
        window.addEventListener('world:ready', settle)
        setTimeout(settle, ms)
    })
}

// Race a promise against a timeout — used so a slow/blocked 3D boot can't trap
// the user; we fall back to the no-3D "lite" quiz instead of waiting forever.
function withTimeout(promise, ms)
{
    return Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))
    ])
}

// Flip the page into no-3D lite mode: branded CSS backdrop instead of the 3D
// room, plus an analytics event so we can measure how many visitors (mostly
// mainland networks throttling our origin) never get the 3D experience.
function enterLiteMode(entry, err)
{
    document.body.classList.add('is-lite')
    track('boot_3d_failed', { entry, reason: String(err?.message || err || 'unknown') })
}

// Preload the post-click code chunks in parallel with asset loading so that
// clicking 现在开始 after everything's ready is truly instant. These chunks are
// three.js-free, so they keep loading even if the heavy 3D bundle is blocked.
const quizReportPromise = Promise.all([loadQuiz(), loadReport()])

function setStartLoading(isLoading, label = '正在准备 AI 工作室…')
{
    startBtn.classList.toggle('is-loading', !!isLoading)
    startBtn.disabled = !!isLoading
    if (isLoading) {
        startBtn.dataset._label = startLabel.textContent
        startLabel.textContent = label
        if (startArrow) startArrow.style.visibility = 'hidden'
    } else {
        if (startBtn.dataset._label) startLabel.textContent = startBtn.dataset._label
        if (startArrow) startArrow.style.visibility = ''
    }
}

async function startQuiz()
{
    if (started) return
    started = true

    // If the background boot isn't done yet, show the spinner. If it finished
    // before the click, skip the spinner entirely — feels instant. After a
    // failed boot we go straight to lite mode, so don't kick a fresh 3D
    // download here just to drive the spinner.
    let raceSettled = false
    Promise.race([
        bootFailedOnce ? Promise.resolve() : bootExperience().catch(() => {}),
        new Promise(r => setTimeout(r, 80))
    ]).then(() => {
        if (!raceSettled && !experience?.world?.ready) setStartLoading(true)
    })

    try {
        // The answer panel must be available — load it first, on its own chunk.
        await bootKeyboard()

        // The 3D world is an enhancement, not a requirement. Give it a bounded
        // window before we START the quiz so the user isn't stuck on the intro
        // waiting for a slow boot. A timeout here does NOT mean lite mode — boot
        // keeps running in the background and the finale will use the world once
        // it's ready (see waitForWorld in _finish). Lite mode is only entered
        // when boot actually fails (bootExperience's catch).
        try {
            if (!bootFailedOnce) await withTimeout(bootExperience(), 12000)
        } catch (e) {
            console.warn('3D still booting — starting quiz now, finale will use it once ready:', e)
        }
        raceSettled = true

        await introAnim.dismiss()

        const [Quiz, Report] = await quizReportPromise
        const quiz = new Quiz({
            keyboard,
            // Live accessors — resolve the experience/world fresh at finale time,
            // so a slow boot that completes during the quiz still drives the
            // cinematic finale instead of being snapshotted as null at start.
            getExperience: () => experience,
            waitForWorld,
            onComplete: (answers) =>
            {
                const report = new Report({ experience })
                report.show(answers)
            }
        })
        quiz.start()
    } catch (err) {
        // Couldn't even get the answer panel up — reset so the user can retry.
        console.error('startQuiz failed:', err)
        raceSettled = true
        setStartLoading(false)
        started = false
    }
}

async function resumeFromId(rawId)
{
    const id = rawId.trim().toUpperCase().replace(/\s+/g, '')
    if (!id) { showResumeHint('请输入报告编号', 'err'); return }

    showResumeHint('正在查询服务器…', 'ok')
    let state = null
    try {
        const mod = await loadReportState()
        state = await mod.loadReportStateSync(id)
    } catch (e) {
        showResumeHint(`查询失败：${e.message || '网络异常'}`, 'err')
        return
    }
    if (!state) {
        showResumeHint(`没找到编号 ${id}`, 'err')
        return
    }
    showResumeHint(`已载入 ${id}，正在进入...`, 'ok')
    if (started) return
    started = true

    setStartLoading(true, '正在载入你的报告…')
    // The report renders fine without 3D — give the world a bounded window so a
    // slow boot doesn't trap the user. A timeout here isn't lite mode; boot keeps
    // running and the camera/screen below will simply no-op until it's ready.
    try {
        if (!bootFailedOnce) await withTimeout(bootExperience(), 12000)
    } catch (e) {
        console.warn('3D still booting — opening report now:', e)
    }

    const p = introAnim.dismiss()
    const Report = await loadReport()
    const openReport = () => {
        const report = new Report({ experience })
        report.resume(state)
    }
    if (p && typeof p.then === 'function') p.then(openReport)
    else openReport()

    const nav = experience?.navigation
    if (nav) {
        const PI = Math.PI
        nav.focusOn({
            spherical: { radius: 13, phi: PI * 0.47, theta: -PI * 0.22 },
            target:    { x: 0.3, y: 3.3, z: -4.5 }
        }, 1.0)
    }
    const world = experience?.world
    if (world?.pcScreen?.start) world.pcScreen.start()
}

function showResumeHint(msg, kind) {
    resumeHint.textContent = msg
    resumeHint.classList.toggle('is-error', kind === 'err')
    resumeHint.classList.toggle('is-ok',    kind === 'ok')
}

// Button is clickable from the moment the page paints — no readiness gate.
startBtn.disabled = false
startBtn.addEventListener('click', () => { startQuiz().catch(err => {
    console.error('startQuiz failed:', err)
    setStartLoading(false)
    started = false
}) })

// Kick off the 3D boot + answer-panel load in the background immediately so that
// by the time the user finishes reading the intro, things are already loaded.
// Both are idempotent — the click handler awaits these same promises. Swallow
// errors here: a blocked 3D boot must not surface as an unhandled rejection, and
// the click handler re-attempts + degrades gracefully.
const kickBackgroundBoot = () => {
    bootKeyboard().catch(() => {})
    bootExperience().catch(() => {})
}
if ('requestIdleCallback' in window) {
    window.requestIdleCallback(kickBackgroundBoot, { timeout: 200 })
} else {
    setTimeout(kickBackgroundBoot, 0)
}
resumeForm.addEventListener('submit', (e) => {
    e.preventDefault()
    resumeFromId(resumeInput.value).catch(err => showResumeHint(err.message || '出错了，请稍后重试', 'err'))
})
